import { ref } from 'vue'
import { Api } from "@/service/api"
import { parse } from "date-fns"

export function useConnectionLoader(route) {
    const isLoading = ref(false)
    const uploadOptions = ref({
        offsetOperation: [
            { name: 'Плюс', type: 'PLUS' },
            { name: 'Минус', type: 'MINUS' },
        ],
        offsetNames: [
            { name: 'Дней', type: 'DAY' },
            { name: 'Месяцев', type: 'MONTH' },
        ]
    })

    async function loadConnection(
        connection,
        selectedYandexAccount,
        yandexUpload,
        selectedVKAccount,
        vkAdsUpload,
        sourceType,
        setYandexAccount,
        setYandexUpload,
        setVKAccount,
        setVKUpload
    ) {
        isLoading.value = true
        const workflowId = route.params.workflow_id
        const connectionId = route.params.connection_id

        try {
            const connectionResponse = await Api.getConnection(workflowId, connectionId)
            const newYandexAccounts = await Api.getUserYandexAcc()
            const newVKAccounts = await Api.getVkAccounts()
            const criteriaResponse = await Api.getFilterCriteria()

            connection.value.name = connectionResponse.name
            connection.value.files_source = []
            connection.value.filter.conditionType = connectionResponse.filter?.condition_type || "AND"

            connectionResponse.sources.forEach((source) => {
                if (source?.self_type === "YandexDirectCampaignStatistics") {
                    mapYandexSource(
                        source,
                        newYandexAccounts,
                        selectedYandexAccount,
                        yandexUpload,
                        sourceType,
                        setYandexAccount,
                        setYandexUpload
                    )
                }
                if (source?.self_type === "VKontakteCampaignStatistics") {
                    mapVKSource(
                        source,
                        newVKAccounts,
                        selectedVKAccount,
                        vkAdsUpload,
                        sourceType,
                        setVKAccount,
                        setVKUpload
                    )
                }
                if (source?.self_type === "GoogleSheetSource") {
                    mapGoogleSheetSource(source, connection, sourceType)
                }
            })

            if (connectionResponse.target?.self_type === "GoogleSheetTarget") {
                mapGoogleSheetTarget(connectionResponse.target, connection)
            }

            if (connectionResponse.filter !== null) {
                mapFilters(connectionResponse.filter, connection, criteriaResponse)
            }

            await syncTabs(connection)

            return connectionResponse
        } catch (error) {
            console.error('Error loading connection:', error)
            throw error
        } finally {
            isLoading.value = false
        }
    }

    function mapYandexSource(
        source,
        accounts,
        selectedYandexAccount,
        yandexUpload,
        sourceType,
        setYandexAccount,
        setYandexUpload
    ) {
        sourceType.value = 'YANDEX_DIRECT_STAT'

        const account = accounts.find(acc => acc.id === source.yandex_account)
        setYandexAccount(account)

        const startDate = { type: "exact", date: null }
        const endDate = { type: "exact", date: null }

        if (source.date_from !== null) {
            startDate.date = parse(source.date_from, 'yyyy-MM-dd', new Date())
            startDate.type = 'exact'
        }
        if (source.offset_from !== null) {
            startDate.length = Math.abs(source.offset_from)
            const operationType = source.offset_from < 0 ? 'MINUS' : 'PLUS'
            startDate.offsetOperation = uploadOptions.value.offsetOperation.find(
                item => item.type === operationType
            )
            startDate.type = 'offset'
            if (source.offset_from_type !== null) {
                startDate.offsetName = uploadOptions.value.offsetNames.find(
                    item => item.type === source.offset_from_type
                )
            }
        }

        if (source.date_to !== null) {
            endDate.date = parse(source.date_to, 'yyyy-MM-dd', new Date())
            endDate.type = 'exact'
        }
        if (source.offset_to !== null) {
            endDate.length = Math.abs(source.offset_to)
            const operationType = source.offset_to < 0 ? 'MINUS' : 'PLUS'
            endDate.offsetOperation = uploadOptions.value.offsetOperation.find(
                item => item.type === operationType
            )
            endDate.type = 'offset'
            if (source.offset_to_type !== null) {
                endDate.offsetName = uploadOptions.value.offsetNames.find(
                    item => item.type === source.offset_to_type
                )
            }
        }

        setYandexUpload({ startDate, endDate })
    }

    function mapVKSource(
        source,
        accounts,
        selectedVKAccount,
        vkAdsUpload,
        sourceType,
        setVKAccount,
        setVKUpload
    ) {
        sourceType.value = 'VKONTAKTE_CAMPAIGN_STAT'

        const account = accounts.find(acc => acc.id === source.vkontakte_account)
        setVKAccount(account)

        const startDate = { type: "exact", date: null }
        const endDate = { type: "exact", date: null }

        if (source.date_from !== null) {
            startDate.date = parse(source.date_from, 'yyyy-MM-dd', new Date())
            startDate.type = 'exact'
        }
        if (source.offset_from !== null && source.offset_from !== 0) {
            startDate.length = Math.abs(source.offset_from)
            const operationType = source.offset_from < 0 ? 'MINUS' : 'PLUS'
            startDate.offsetOperation = uploadOptions.value.offsetOperation.find(
                item => item.type === operationType
            )
            startDate.type = 'offset'
            if (source.offset_from_type !== null) {
                startDate.offsetName = uploadOptions.value.offsetNames.find(
                    item => item.type === source.offset_from_type
                )
            }
        }

        if (source.date_to !== null) {
            endDate.date = parse(source.date_to, 'yyyy-MM-dd', new Date())
            endDate.type = 'exact'
        }
        if (source.offset_to !== null && source.offset_to !== 0) {
            endDate.length = Math.abs(source.offset_to)
            const operationType = source.offset_to < 0 ? 'MINUS' : 'PLUS'
            endDate.offsetOperation = uploadOptions.value.offsetOperation.find(
                item => item.type === operationType
            )
            endDate.type = 'offset'
            if (source.offset_to_type !== null) {
                endDate.offsetName = uploadOptions.value.offsetNames.find(
                    item => item.type === source.offset_to_type
                )
            }
        }

        setVKUpload({ startDate, endDate })
    }

    function mapGoogleSheetSource(source, connection, sourceType) {
        sourceType.value = 'GOOGLE_SHEETS'
        connection.value.files_source.push({
            ...source,
            file: {
                id: source.spreadsheet_id,
                tab_id: source.tab_id,
                tab_name: source.tab_name,
                spreadsheet_name: source.spreadsheet_name,
            },
            tabs: [],
            id: source.spreadsheet_id,
            tab: { sheetId: source.tab_id, sheetTitle: source.tab_name },
            name: source.spreadsheet_name,
            isLoading: false
        })
    }

    function mapGoogleSheetTarget(target, connection) {
        connection.value.file_target = {
            ...target,
            file: {
                id: target.spreadsheet_id,
                tab_id: target.tab_id,
                tab_name: target.tab_name,
                spreadsheet_name: target.spreadsheet_name,
            },
            tabs: [],
            id: target.spreadsheet_id,
            tab: { sheetId: target.tab_id, sheetTitle: target.tab_name },
            name: target.spreadsheet_name,
            isLoading: false
        }
    }

    function mapFilters(filterResponse, connection, criteriaResponse) {
        connection.value.filter.conditions = []
        for (const condition of filterResponse.conditions) {
            connection.value.filter.conditions.push({
                column: {
                    column_index: condition.column_index,
                    column_letter: condition.column_letter,
                    column_value: condition.column_name
                },
                criteria: criteriaResponse.find(
                    item => item.criteria_type === condition.criteria
                ),
                filter_value: {
                    data: condition.value,
                    type: condition.value_type
                }
            })
        }
    }

    async function syncTabs(connection) {
        await Promise.all(
            connection.value.files_source.map(file => syncSourceTabs(file.id, connection))
        )
        await syncTargetTabs(connection)
    }

    async function syncSourceTabs(spreadsheet_id, connection) {
        const file_source = connection.value.files_source.find(
            file => file.id === spreadsheet_id
        )
        if (file_source) {
            file_source.isLoading = true
            file_source.tabs = await Api.getSpreadSheetTabs(spreadsheet_id)
            file_source.isLoading = false
        }
    }

    async function syncTargetTabs(connection) {
        if (connection.value.file_target.id) {
            connection.value.file_target.isLoading = true
            connection.value.file_target.tabs = await Api.getSpreadSheetTabs(
                connection.value.file_target.id
            )
            connection.value.file_target.isLoading = false
        }
    }

    return {
        isLoading,
        uploadOptions,
        loadConnection
    }
}